import {
  onAuthStateChanged,
  reload,
  signInWithEmailAndPassword,
  signOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  createCompany as createCompanyAPI,
  subscribeToCompany,
} from '../api/companies';
import { subscribeToMember } from '../api/members';
import type {
  Company,
  CompanyMember,
  MeasuringUnit,
  UserCompany,
  UserRole,
} from '../api/models';
import { getUserCompanies } from '../api/users';
import { auth } from '../config/firebase';
import type { AuthUser, RolePermissions } from '../types/auth';

export interface AuthContextType {
  user: AuthUser | null;
  company: Company | null;
  userCompanies: UserCompany[];
  loading: boolean;
  error: string | null;

  // Auth actions
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;

  // Company actions
  switchCompany: (companyId: string) => void;
  createCompany: (companyData: {
    name: string;
    email: string;
    registrationNumber: string;
    timezone?: string;
    phone?: string;
    address?: string;
    measurements?: MeasuringUnit;
  }) => Promise<Company>;

  // Auth error handling
  retryAuth: () => Promise<void>;

  // Permission checks
  hasPermission: (permission: keyof RolePermissions) => boolean;
  isAdmin: () => boolean;
  isTechnician: () => boolean;
  isOwner: () => boolean;

  // Convenience getters
  isAuthenticated: boolean;
  hasCompany: boolean;
  hasMultipleCompanies: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [userCompanies, setUserCompanies] = useState<UserCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const companyUnsubscribeRef = useRef<(() => void) | null>(null);
  const memberUnsubscribeRef = useRef<(() => void) | null>(null);
  const currentFirebaseUserRef = useRef<FirebaseUser | null>(null);

  const updateCompanyData = useCallback((updatedCompany: Company) => {
    setUserCompanies((prev) =>
      prev.map((uc) =>
        uc.company.id === updatedCompany.id
          ? { ...uc, company: updatedCompany }
          : uc
      )
    );
    setCompany(updatedCompany);
    setUser((prevUser) => {
      if (!prevUser) return prevUser;
      return {
        ...prevUser,
        companyId: updatedCompany.id,
        companyName: updatedCompany.name,
      };
    });
  }, []);

  const updateUserRole = useCallback((companyId: string, newRole: UserRole) => {
    setUserCompanies((prev) =>
      prev.map((uc) =>
        uc.company.id === companyId ? { ...uc, role: newRole } : uc
      )
    );
    setUser((prevUser) => {
      if (!prevUser) return prevUser;
      return { ...prevUser, role: newRole };
    });
  }, []);

  const resetCompanyData = useCallback((companyId: string) => {
    console.debug('[Auth] Resetting company data for company:', companyId);
    setUserCompanies((prev) =>
      prev.filter((uc) => uc.company.id !== companyId)
    );
    setCompany(null);
    setUser((prevUser) => {
      if (!prevUser) return prevUser;
      return {
        ...prevUser,
        companyId: undefined,
        companyName: undefined,
        role: undefined,
      };
    });
  }, []);

  const activateCompany = useCallback(
    (selectedUserCompany: UserCompany, userId: string) => {
      const { company: selectedCompany, role: selectedRole } =
        selectedUserCompany;

      if (companyUnsubscribeRef.current) {
        companyUnsubscribeRef.current();
        companyUnsubscribeRef.current = null;
      }
      if (memberUnsubscribeRef.current) {
        memberUnsubscribeRef.current();
        memberUnsubscribeRef.current = null;
      }

      const unsubscribeCompany = subscribeToCompany(
        selectedCompany.id,
        (updatedCompany: Company | null) => {
          if (updatedCompany) {
            updateCompanyData(updatedCompany);
          } else {
            resetCompanyData(selectedCompany.id);
          }
        }
      );

      const unsubscribeMember = subscribeToMember(
        selectedCompany.id,
        userId,
        (member: CompanyMember | null) => {
          if (member) {
            updateUserRole(selectedCompany.id, member.role);
          } else {
            console.debug(
              '[Auth] User is no longer a member of company:',
              selectedCompany.id
            );
            resetCompanyData(selectedCompany.id);
          }
        }
      );

      updateCompanyData(selectedCompany);
      updateUserRole(selectedCompany.id, selectedRole);

      companyUnsubscribeRef.current = unsubscribeCompany;
      memberUnsubscribeRef.current = unsubscribeMember;
    },
    [updateCompanyData, updateUserRole, resetCompanyData]
  );

  const initializeAuth = useCallback(
    async (firebaseUser: FirebaseUser) => {
      console.debug('[Auth] Firebase user detected:', firebaseUser.uid);
      setLoading(true);
      setError(null);

      if (companyUnsubscribeRef.current) {
        companyUnsubscribeRef.current();
        companyUnsubscribeRef.current = null;
      }
      if (memberUnsubscribeRef.current) {
        memberUnsubscribeRef.current();
        memberUnsubscribeRef.current = null;
      }

      try {
        const enhancedUser: AuthUser = {
          uid: firebaseUser.uid,
          email: firebaseUser.email || undefined,
          isEmailVerified: firebaseUser.emailVerified,
          displayName: firebaseUser.displayName || 'Unknown User',
        };
        setUser(enhancedUser);

        console.debug('[Auth] Fetching companies...');
        const result = await getUserCompanies(firebaseUser.uid);

        if (result.error) {
          throw new Error(result.error);
        }

        const companies = result.data || [];
        console.debug('[Auth] Companies loaded:', companies.length);
        setUserCompanies(companies);

        if (companies.length === 1) {
          activateCompany(companies[0], firebaseUser.uid);
        } else {
          // No companies → setup page; multiple → company selector
          setCompany(null);
        }

        console.debug('[Auth] Initialization complete');
      } catch (err) {
        console.error('[Auth] Auth initialization failed:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to load account data'
        );
      } finally {
        setLoading(false);
      }
    },
    [activateCompany]
  );

  useEffect(() => {
    const unsubscribeFromAuthStateChanged = onAuthStateChanged(
      auth,
      async (firebaseUser) => {
        currentFirebaseUserRef.current = firebaseUser;

        if (firebaseUser) {
          if (firebaseUser.emailVerified) {
            await initializeAuth(firebaseUser);
          } else {
            console.log('[Auth] User email not verified');
            setUser({
              uid: firebaseUser.uid,
              email: firebaseUser.email || undefined,
              displayName: firebaseUser.displayName || 'Unknown User',
              isEmailVerified: firebaseUser.emailVerified,
            });
            setCompany(null);
            setUserCompanies([]);
            setError(null);
            setLoading(false);
          }
        } else {
          console.debug('[Auth] User signed out');
          setUser(null);
          setCompany(null);
          setUserCompanies([]);
          setError(null);
          setLoading(false);
        }
      }
    );

    return () => {
      unsubscribeFromAuthStateChanged();
      if (companyUnsubscribeRef.current) {
        companyUnsubscribeRef.current();
        companyUnsubscribeRef.current = null;
      }
      if (memberUnsubscribeRef.current) {
        memberUnsubscribeRef.current();
        memberUnsubscribeRef.current = null;
      }
    };
  }, [retryCount, initializeAuth]); // re-subscribe when retryAuth triggers a retryCount bump

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    await signOut(auth);
  };

  const retryAuth = async () => {
    console.log('[Auth] Retrying auth initialization...');
    const currentUser = auth.currentUser;

    if (!currentUser) {
      setRetryCount((prev) => prev + 1);
      return;
    }

    try {
      await reload(currentUser);

      const reloadedUser = auth.currentUser;
      if (!reloadedUser) {
        console.error('[Auth] User not found after reload');
        return;
      }

      currentFirebaseUserRef.current = reloadedUser;

      if (reloadedUser.emailVerified) {
        console.log('[Auth] Email verified, initializing auth...');
        await initializeAuth(reloadedUser);
      } else {
        console.log('[Auth] Email still not verified after reload');
        setUser((prevUser) => {
          if (!prevUser) return prevUser;
          return { ...prevUser, isEmailVerified: reloadedUser.emailVerified };
        });
      }
    } catch (err) {
      console.error('[Auth] Error reloading user:', err);
      setError(
        err instanceof Error ? err.message : 'Failed to reload user data'
      );
    }
  };

  const switchCompany = useCallback(
    (companyId: string) => {
      if (!user?.uid || !user?.isEmailVerified) {
        console.warn(
          '[Auth] Cannot switch company: user not ready or email not verified'
        );
        return;
      }
      const selectedUserCompany = userCompanies.find(
        (uc) => uc.company.id === companyId
      );
      if (selectedUserCompany) {
        activateCompany(selectedUserCompany, user.uid);
      }
    },
    [user, userCompanies, activateCompany]
  );

  const createCompany = async (companyData: {
    name: string;
    email: string;
    registrationNumber: string;
    phone?: string;
    address?: string;
    timezone?: string;
    measurements?: MeasuringUnit;
  }): Promise<Company> => {
    if (!user) throw new Error('User must be authenticated');
    if (!user.isEmailVerified)
      throw new Error('Email must be verified to create a company');

    try {
      const newCompany = await createCompanyAPI(companyData);
      const newUserCompany: UserCompany = {
        company: newCompany,
        role: 'admin',
      };

      setUserCompanies((prev) => [...prev, newUserCompany]);

      console.debug('[Auth] Activating new company:', newCompany.id);
      activateCompany(newUserCompany, user.uid);

      return newCompany;
    } catch (err) {
      console.error('Error creating company:', err);
      throw err;
    }
  };

  const isAdmin = (): boolean => user?.role === 'admin';
  const isTechnician = (): boolean => user?.role === 'technician';
  const isOwner = useCallback((): boolean => {
    if (!user?.uid || !company) return false;
    return company.ownerId === user.uid;
  }, [user, company]);

  const permissions: RolePermissions = {
    // Pool Management
    canViewPools: true,
    canCreatePools: isAdmin(),
    canEditPools: isAdmin(),
    canDeletePools: isAdmin(),
    canViewPoolRates: isAdmin(),

    // Pool Service Management
    canCreatePoolServices: true,
    canEditPoolServices: true,
    canDeletePoolServices: isAdmin(),

    // Customer Management
    canViewCustomers: isAdmin(),
    canCreateCustomers: isAdmin(),
    canEditCustomers: isAdmin(),
    canDeleteCustomers: isAdmin(),

    // Repair Management
    canCreateRepairs: true,
    canEditRepairs: true,
    canDeleteRepairs: isAdmin(),

    // Billing & Financial
    canViewBilling: isAdmin(),
    canEditBilling: isAdmin(),
    canViewFinancialReports: isAdmin(),

    // Company Settings
    canManageCompanyDetails: isAdmin(),
    canManageCompanySettings: isAdmin(),
    canManageSubscription: isOwner(),
    canManageTeam: isAdmin(),
    canManageChemicals: isAdmin(),

    // Reports & Analytics
    canViewAllReports: isAdmin(),
    canViewChemicalUsageReports: isAdmin(),

    // Advanced Features
    canAccessAdvancedFeatures: isAdmin(),

    // Multi-company & data
    canSwitchCompany: userCompanies.length > 1,
    canImportData: isOwner(),
  };

  const hasPermission = (permission: keyof RolePermissions): boolean => {
    return permissions[permission] || false;
  };

  const value: AuthContextType = {
    user,
    company,
    userCompanies,
    loading,
    error,

    login,
    logout,

    switchCompany,
    createCompany,

    retryAuth,

    hasPermission,
    isAdmin,
    isTechnician,
    isOwner,

    isAuthenticated: !!user,
    hasCompany: !!company,
    hasMultipleCompanies: userCompanies.length > 1,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Convenience hooks for common use cases
export const useAuthUser = (): AuthUser | null => {
  const { user } = useAuth();
  return user;
};

export const useAuthCompany = (): Company | null => {
  const { company } = useAuth();
  return company;
};

export const useAuthLoading = (): boolean => {
  const { loading } = useAuth();
  return loading;
};

export const useAuthData = () => {
  const { user, company, loading, isAuthenticated, hasCompany } = useAuth();
  return { user, company, loading, isAuthenticated, hasCompany };
};
